import type { TFunction } from 'i18next';

/**
 * Presentation helpers for trips. Pure and tested, so the numbers a customer
 * reads off a monthly report are not decided inside JSX.
 *
 * WHAT THIS FILE DOES NOT DO, and why:
 *
 *   - **It never divides a speed by ten.** `maxSpeedKmh` / `avgSpeedKmh` arrive
 *     as km/h; the tenths are a storage detail the server already converted (S1,
 *     `types.ts`). A second division here would render 14 km/h as 1.4 and look
 *     entirely reasonable doing it.
 *   - **It never turns a coordinate into an address.** There is no geocoder in
 *     the stack, and the canvas's "Wadajir → Elasha Biyaha" strings are
 *     placeholders. A coordinate is what a client forwards to a driver, so a
 *     coordinate is what we show.
 */

/** Coordinates at FIVE decimal places — ~1 m, and the precision a client can
 *  paste into any maps app. One formatter, so the trip screen, the replay
 *  readout and the live vehicle card can never disagree by a digit. */
export function formatCoords(lat: number, lon: number): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

/**
 * A geo: URI, which Android hands to whatever maps app the user actually has —
 * not a hardcoded google.com link, which is a worse answer on a phone and a
 * third-party call we did not need to make. `q=` carries the pin.
 */
export function mapsUrl(lat: number, lon: number): string {
  const q = `${lat.toFixed(5)},${lon.toFixed(5)}`;
  return `geo:${q}?q=${q}`;
}

/**
 * Distance in the unit the number deserves: metres below a kilometre (a 380 m
 * trip is not "0.4 km"), one decimal of a kilometre above it. Integer metres in,
 * always — the wire carries no float.
 */
export function formatDistance(t: TFunction, metres: number): string {
  const m = Math.max(0, Math.round(metres));
  if (m < 1000) return t('trips.unit.metres', { value: m });
  return t('trips.unit.km', { value: (m / 1000).toFixed(1) });
}

/**
 * Duration as h/m, or m/s under an hour — never a bare second count, and never
 * "0h 16m". Seconds show only under a minute, where they are the whole story.
 */
export function formatDuration(t: TFunction, seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return t('trips.unit.hm', { h, m });
  if (m > 0) return t('trips.unit.m', { m });
  return t('trips.unit.s', { s });
}

/** Speed to one decimal — the precision the wire carries. */
export function formatSpeed(t: TFunction, kmh: number): string {
  return t('trips.unit.kmh', { value: kmh.toFixed(1) });
}

/**
 * TIME ZONE — every function below resolves a wall-clock quantity (a day, a
 * midnight boundary, an HH:MM) in an EXPLICIT IANA `timeZone`, never the browser's.
 *
 * This is the fix for the day-boundary defect: `new Date(y, m, d)` and
 * `getHours()` resolve in whatever zone the *machine* sits in — EAT on Mahad's
 * workstation, UTC on CI, CAT for a Zambian tenant — so "16 Aug" meant a different
 * 24 hours for each viewer, and a Mogadishu trip at 01:30 EAT (22:30Z the previous
 * day) fell into the wrong day for anyone not sitting in EAT. The `timeZone` here
 * is the TENANT's zone, threaded from `/api/me` (see `useTenantTimeZone`), so the
 * window is the same regardless of who is looking or where CI runs.
 *
 * The offset is read from `Intl` at the relevant instant — never hand-rolled — so
 * DST transitions are handled by the platform's tz database, not by a hardcoded
 * `+03:00` that would be wrong for CAT and would rot at the next DST rule change.
 */

/** The wall-clock parts of `ms` as seen in `timeZone`. */
function zonedParts(ms: number, timeZone: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(new Date(ms))) map[p.type] = p.value;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    // Intl can emit "24" for midnight in the en-US hour12:false path.
    hour: map.hour === '24' ? 0 : Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

/** How far `timeZone` is ahead of UTC at instant `utcMs`, in ms (wall − UTC). */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const p = zonedParts(utcMs, timeZone);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asIfUtc - utcMs;
}

/** The UTC instant at which the wall clock in `timeZone` reads midnight on the
 *  given civil date. Day/month/year overflow is normalised by `Date.UTC`, so
 *  `day + 1` correctly rolls into the next month. */
function zonedMidnightUtc(year: number, month: number, day: number, timeZone: string): number {
  const guess = Date.UTC(year, month - 1, day, 0, 0, 0);
  const off1 = zoneOffsetMs(guess, timeZone);
  let candidate = guess - off1;
  // One correction handles a DST jump straddling the guessed instant.
  const off2 = zoneOffsetMs(candidate, timeZone);
  if (off2 !== off1) candidate = guess - off2;
  return candidate;
}

/**
 * Wall-clock HH:MM in the tenant's `timeZone`, digits only — no locale word, so
 * there is no silent Somali→English fallback to leak (the reason `relativeTime.ts`
 * refuses `Intl.RelativeTimeFormat`).
 */
export function formatClock(locale: string, ms: number, timeZone: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  };
  try {
    return new Intl.DateTimeFormat(locale || 'en', opts).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', opts).format(ms);
  }
}

/** HH:MM:SS in the tenant's `timeZone` — the readout where a second matters. */
export function formatClockSeconds(locale: string, ms: number, timeZone: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone,
  };
  try {
    return new Intl.DateTimeFormat(locale || 'en', opts).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', opts).format(ms);
  }
}

/** A calendar day heading — weekday + day + month — in the tenant's `timeZone`. */
export function formatDayHeading(locale: string, ms: number, timeZone: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone,
  };
  try {
    return new Intl.DateTimeFormat(locale || 'en', opts).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', opts).format(ms);
  }
}

/** `YYYY-MM-DD` for the instant `ms` as it falls in the tenant's `timeZone` — the
 *  value a native `<input type="date">` uses and the key trips are grouped by, so
 *  a day boundary is the TENANT's midnight rather than the viewing machine's. */
export function localDayKey(ms: number, timeZone: string): string {
  const p = zonedParts(ms, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** The [from, to) instants of a calendar day in the tenant's `timeZone`, as ISO
 *  strings for the query. A trip stored in UTC is selected by the tenant's day. */
export function localDayRange(
  dayKey: string,
  timeZone: string,
): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const from = zonedMidnightUtc(year, month, day, timeZone);
  const to = zonedMidnightUtc(year, month, day + 1, timeZone);
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}
